import { BRAND_MAP_COLOR_PRESETS, normalizeHexColor } from "./branding";

export const TSUNAMI_RANKS = [1, 2, 3, 4, 5];
export const TSUNAMI_NEUTRAL_FILL = "#e5e7eb";
export const TSUNAMI_STORAGE_KEY = "electionmap.tsunami.v1";

function normalizeText(value) {
  return String(value ?? "").trim();
}

export function normalizeTsunamiRank(value, fallback = 1) {
  const rank = Number(value);
  if (TSUNAMI_RANKS.includes(rank)) return rank;
  return TSUNAMI_RANKS.includes(fallback) ? fallback : 1;
}

export function getTsunamiEntityName(result) {
  if (!result || typeof result !== "object") return "";
  return normalizeText(result.party) || normalizeText(result.candidate_name);
}

export function getTsunamiEntityIdentity(result) {
  if (!result || typeof result !== "object") return "";
  return normalizeText(result.party_code) || normalizeText(result.candidate_code) || getTsunamiEntityName(result);
}

function toFiniteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function compareText(a, b) {
  return normalizeText(a).localeCompare(normalizeText(b), "cs", { sensitivity: "base" });
}

export function compareTsunamiRankResults(a, b) {
  const aVotes = toFiniteNumber(a && a.votes);
  const bVotes = toFiniteNumber(b && b.votes);
  if (aVotes !== bVotes) return (bVotes ?? Number.NEGATIVE_INFINITY) - (aVotes ?? Number.NEGATIVE_INFINITY);

  const aPct = toFiniteNumber(a && a.percentage);
  const bPct = toFiniteNumber(b && b.percentage);
  if (aPct !== bPct) return (bPct ?? Number.NEGATIVE_INFINITY) - (aPct ?? Number.NEGATIVE_INFINITY);

  const identityCompare = compareText(getTsunamiEntityIdentity(a), getTsunamiEntityIdentity(b));
  if (identityCompare !== 0) return identityCompare;

  const nameCompare = compareText(getTsunamiEntityName(a), getTsunamiEntityName(b));
  if (nameCompare !== 0) return nameCompare;

  return Number(a && a.__tsunamiIndex ? a.__tsunamiIndex : 0) - Number(b && b.__tsunamiIndex ? b.__tsunamiIndex : 0);
}

export function getTsunamiRankEntry(resultList, rank) {
  const normalizedRank = normalizeTsunamiRank(rank);
  const ranked = (Array.isArray(resultList) ? resultList : [])
    .map((row, index) => ({ ...(row && typeof row === "object" ? row : {}), __tsunamiIndex: index }))
    .filter((row) => toFiniteNumber(row.votes) != null && getTsunamiEntityIdentity(row))
    .sort(compareTsunamiRankResults);

  const entity = ranked[normalizedRank - 1] || null;
  if (!entity) return null;

  return {
    entity,
    rank: normalizedRank,
    entityIdentity: getTsunamiEntityIdentity(entity),
    entityName: getTsunamiEntityName(entity),
    votes: toFiniteNumber(entity.votes),
    percentage: toFiniteNumber(entity.percentage),
  };
}

export function buildTsunamiRankByPrecinct(results, rank) {
  const next = new Map();
  for (const row of Array.isArray(results) ? results : []) {
    const precinctId = normalizeText(row && row.okrsek);
    if (!precinctId) continue;
    const entry = getTsunamiRankEntry(row && row.results, rank);
    if (entry) next.set(precinctId, { ...entry, precinctId });
  }
  return next;
}

export function getTsunamiPaletteColors(presets = BRAND_MAP_COLOR_PRESETS) {
  const colors = (Array.isArray(presets) ? presets : [])
    .map((item) => normalizeHexColor(item && item.hex))
    .filter(Boolean);
  return colors.length > 0 ? colors : ["#E6007E", "#1666d7", "#e21039", "#009640", "#f97316"];
}

export function sanitizeTsunamiColorOverrides(overrides) {
  if (!overrides || typeof overrides !== "object") return {};
  const clean = {};
  for (const [identity, value] of Object.entries(overrides)) {
    const key = normalizeText(identity);
    const color = normalizeHexColor(value);
    if (key && color) clean[key] = color;
  }
  return clean;
}

export function buildTsunamiEntityColorMap(rankEntries, colorOverrides = {}, paletteColors = getTsunamiPaletteColors()) {
  const overrides = sanitizeTsunamiColorOverrides(colorOverrides);
  const colors = (Array.isArray(paletteColors) ? paletteColors : [])
    .map(normalizeHexColor)
    .filter(Boolean);
  const resolvedColors = colors.length > 0 ? colors : getTsunamiPaletteColors();
  const entities = new Map();

  for (const entry of Array.isArray(rankEntries) ? rankEntries : []) {
    const identity = normalizeText(entry && entry.entityIdentity);
    if (!identity || entities.has(identity)) continue;
    entities.set(identity, {
      entityIdentity: identity,
      entityName: normalizeText(entry && entry.entityName) || identity,
    });
  }

  const sortedEntities = Array.from(entities.values()).sort((a, b) => {
    const byName = compareText(a.entityName, b.entityName);
    if (byName !== 0) return byName;
    return compareText(a.entityIdentity, b.entityIdentity);
  });

  const map = new Map();
  sortedEntities.forEach((entity, index) => {
    map.set(entity.entityIdentity, {
      ...entity,
      color: overrides[entity.entityIdentity] || resolvedColors[index % resolvedColors.length],
      isOverride: Boolean(overrides[entity.entityIdentity]),
    });
  });
  return map;
}

export function getTsunamiFillColor(rankEntry, colorMap, neutralFill = TSUNAMI_NEUTRAL_FILL) {
  if (!rankEntry || !rankEntry.entityIdentity || !colorMap || typeof colorMap.get !== "function") {
    return neutralFill;
  }
  return colorMap.get(rankEntry.entityIdentity)?.color || neutralFill;
}
